import type { AutomationStep } from './automation-workflow.model';

/**
 * @desc The zod-free half of the automations models, as `FragmentRoutes` is for the fragments
 * contract (round 3 lane 8, H2-312): the enum values the admin list pages validate their URL
 * search against. TanStack Router keeps every route's `validateSearch` in the portal's entry
 * chunk, and the model namespaces compile to one import-time block each, schemas included, so a
 * search parser that read `AutomationRun.statuses` put Zod's automation schemas on every first
 * load. The parsers import this module instead; the models re-export each member under its
 * existing name, so producers and the schemas keep one source.
 */
export namespace AutomationFilters {
  /** @desc Identity statuses shared by workflows, collectors and processors (§7). */
  export const entityStatuses = {
    Draft: 'draft',
    Ready: 'ready',
    Archived: 'archived',
  } as const;

  /** @desc `AutomationRun.statuses`. */
  export const runStatuses = {
    Queued: 'queued',
    Running: 'running',
    Succeeded: 'succeeded',
    Failed: 'failed',
    Cancelled: 'cancelled',
  } as const;

  /** @desc `AutomationRun.triggerKinds`. */
  export const runTriggerKinds = {
    Schedule: 'schedule',
    Manual: 'manual',
    Test: 'test',
  } as const;

  /** @desc `AutomationCollector.kinds`. */
  export const collectorKinds = {
    Web: 'web',
    Api: 'api',
    Rss: 'rss',
    JsonApi: 'json-api',
    Search: 'search',
    Excel: 'excel',
  } as const;

  /** @desc `AutomationProcessor.types`: the step types a processor may wrap (D9). */
  export const processorTypes = [
    'parse.pdf',
    'parse.csv',
    'parse.xlsx',
    'transform.python',
    'summarise',
    'summarise.ai',
    'extract',
    'extract.passages',
    /** @desc One taxonomy config reused by every feed workflow (`add-news-collectors` D5). */
    'news.tag',
    'index.pages',
  ] as const satisfies readonly AutomationStep.Type[];

  /** @desc `AutomationWire.MAX_LIST`: the upper bound on every list the router serves. */
  export const MAX_LIST = 200;
}
